import { AdminRole } from '@low6-pickem/api-types';

interface AdminTokenPayload {
  user_id: string;
  email: string;
  role: AdminRole;
  iat?: number;
  exp?: number;
}

/**
 * Get the current admin user's token from localStorage
 */
export function getAdminToken(): string | null {
  return localStorage.getItem('access_token');
}

/**
 * Decode the admin JWT token to get user information
 * JWT tokens are base64url encoded JSON, so we can decode them manually
 */
export function getAdminUser(): AdminTokenPayload | null {
  const token = getAdminToken();
  if (!token) {
    return null;
  }

  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodedPayload) as AdminTokenPayload;
  } catch (error) {
    console.error('Failed to decode admin token:', error);
    return null;
  }
}

/**
 * Check if the current user has a specific role
 */
export function hasRole(role: AdminRole): boolean {
  const user = getAdminUser();
  return user?.role === role;
}

/**
 * Check if the current user is a super admin
 */
export function isSuperAdmin(): boolean {
  return hasRole(AdminRole.SUPER_ADMIN);
}

/**
 * Check if the current user is a game manager
 */
export function isGameManager(): boolean {
  return hasRole(AdminRole.GAME_MANAGER);
}

/**
 * Check if the current user is a member
 */
export function isMember(): boolean {
  return hasRole(AdminRole.MEMBER);
}

/**
 * Get the current user's role
 */
export function getCurrentRole(): AdminRole | null {
  const user = getAdminUser();
  return user?.role || null;
}
